import React, { useEffect } from 'react';
import {
  MODAL_SCHEDULE_CREATE_EDIT, MODAL_UPGRADE_LICENSE, MODAL_SCHEDULER_INACTIVE_WARNING, MODAL_SCHEDULER_NO_WS_WARNING,
  MODAL_SCHEDULER_OFFLINE_AGENT_WARNING,
  MODAL_TAGS_IN_AGENTS,
  MODAL_SCHEDULE_DETAIL
} from 'store/modals/modals';
import upgradeLicenseImage from 'Images/scheduler-license.png';
import ReduxModal from 'Common/Components/ReduxModal';
import UpgradeLicense from 'Common/Components/UpgradeLicense';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllowedByFeature, selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_SCHEDULER, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { ACTIONS } from 'tracking/GA/constants';
import {
  selectIsFetching, selectData, selectLimitReached, selectSelectedSection, selectAssetsTags,
  selectVulnsTags, selectServicesTags, selectSchedulerAssetsTags, selectSchedulerVulnsTags,
  selectSchedulerServicesTags
} from 'store/Schedule/selectors';
import { getAllUserAgents, setIsCreateSchedule, resetState, setTagsInScheduler, addTagsToScheduler } from 'store/Schedule/actions';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { redirect } from 'store/Router/actions';
import { closeModal, openModal } from 'store/modals/actions';
import selectModal from 'store/modals/selectors';
import CreateEditSchedule from './components/CreateEditSchedule';
import ActionBar from './components/ActionBar';
import NoWsWarning from './components/NoWsWarning';
import { Wrapper } from './styled';
import OfflineAgent from './components/OfflineAgent';
import { getWorkspaces } from 'Screens/Contextualization/Workspaces/actions/Actions';
import SECTIONS from './components/ActionBar/components/SectionSelector/Sections';
import TagsModal from 'Screens/Automation/Agents/components/AgentDetail/components/TagsModal';
import { selectGeneralHostsTags, selectGeneralServicesTags, selectGeneralVulnsTags } from 'store/Tags/selectors';
import SchedulerDetail from './components/SchedulerDetail';
import InactiveScheduler from './components/InactiveScheduler';

const Schedule = () => {
  const showCreateEditModal = useSelector((state) => selectModal(state, MODAL_SCHEDULE_CREATE_EDIT));
  const dispatch = useDispatch();
  const allowedByFeature = useSelector((state) => (selectAllowedByFeature(state, FEATURE_SCHEDULER, PERMISSION_TYPES.READ)));
  const workspaceSelected = useSelector(selectCurrentWorkspace);
  const data = useSelector((state) => (selectData(state)));
  const isFetching = useSelector((state) => (selectIsFetching(state)));
  const limitReached = useSelector(selectLimitReached);
  const noSchedule = data.rows && (data.rows.length === 0 && !isFetching);
  const isAllowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_SCHEDULER, PERMISSION_TYPES.READ));
  const isAllowed = allowedByFeature && isAllowedByRole;
  const showNoWsWarning = useSelector((state) => selectModal(state, MODAL_SCHEDULER_NO_WS_WARNING));
  const showOfflineAgentWarning = useSelector((state) => selectModal(state, MODAL_SCHEDULER_OFFLINE_AGENT_WARNING));
  const selectedSection = useSelector(selectSelectedSection);
  const selectedComponent = SECTIONS.find((e) => e.value === selectedSection)?.component;
  const showTagsModal = useSelector((state) => selectModal(state, MODAL_TAGS_IN_AGENTS));
  const showDetail = useSelector((state) => selectModal(state, MODAL_SCHEDULE_DETAIL));
  const vulnsOptions = useSelector(selectGeneralVulnsTags);
  const hostsOptions = useSelector(selectGeneralHostsTags);
  const servicesOptions = useSelector(selectGeneralServicesTags);
  const assetsTags = useSelector(selectSchedulerAssetsTags);
  const vulnsTags = useSelector(selectSchedulerVulnsTags);
  const servicesTags = useSelector(selectSchedulerServicesTags);
  const assetsTagsAdded = useSelector(selectAssetsTags);
  const vulnsTagsAdded = useSelector(selectVulnsTags);
  const servicesTagsAdded = useSelector(selectServicesTags);
  const showInactiveSchedulerWarning = useSelector((state) => selectModal(state, MODAL_SCHEDULER_INACTIVE_WARNING));

  useEffect(() => {
    if (!isAllowed) dispatch(redirect('/404'));
    dispatch(getAllUserAgents());
  }, [dispatch, isAllowed, workspaceSelected]);

  useEffect(() => {
    dispatch(getWorkspaces(false, false));
  }, [dispatch]);

  useEffect(() => () => {
    dispatch(resetState());
    dispatch(closeModal(MODAL_SCHEDULER_NO_WS_WARNING));
    dispatch(closeModal(MODAL_SCHEDULER_OFFLINE_AGENT_WARNING));
    dispatch(closeModal(MODAL_SCHEDULE_DETAIL));
    dispatch(closeModal(MODAL_SCHEDULE_CREATE_EDIT));
  }, [dispatch]);

  const showCreate = () => {
    if (limitReached) {
      dispatch(openModal(MODAL_UPGRADE_LICENSE));
    } else {
      dispatch(setIsCreateSchedule(true));
      dispatch(openModal(MODAL_SCHEDULE_CREATE_EDIT));
    }
  };

  const showEdit = () => {
    dispatch(setIsCreateSchedule(false));
    dispatch(openModal(MODAL_SCHEDULE_CREATE_EDIT));
  };

  const saveTags = (data) => {
    const newTags = Object.assign({}, data);
    if (showCreateEditModal) dispatch(addTagsToScheduler(newTags));
    else dispatch(setTagsInScheduler(data));
  };

  return (
    <>
      { showCreateEditModal ? <CreateEditSchedule /> : null }
      <Wrapper emptySchedule={ noSchedule }>
        <ActionBar toggleModalCreateSchedule={ showCreate } toggleModalEditSchedule={ showEdit } />
        { selectedComponent }
        <ReduxModal id={ MODAL_UPGRADE_LICENSE }>
          <UpgradeLicense id={ MODAL_UPGRADE_LICENSE } image={ upgradeLicenseImage } descriptionIntlId="upgradeLicense.schedule.description" trackingShow={ ACTIONS.showSchedule } trackingClick={ ACTIONS.clickSchedule } />
        </ReduxModal>
        { showNoWsWarning && <NoWsWarning /> }
        { showOfflineAgentWarning && <OfflineAgent /> }
        { showTagsModal &&
          <TagsModal
            vulnsOptions={ vulnsOptions }
            hostsOptions={ hostsOptions }
            servicesOptions={ servicesOptions }
            assetsTagsAdded={ showCreateEditModal ? assetsTags : assetsTagsAdded }
            vulnsTagsAdded={ showCreateEditModal ? vulnsTags : vulnsTagsAdded }
            servicesTagsAdded={ showCreateEditModal ? servicesTags : servicesTagsAdded }
            saveTags={ saveTags }
          />
        }
        { showInactiveSchedulerWarning && <InactiveScheduler /> }
        { showDetail && <SchedulerDetail /> }
      </Wrapper>
    </>
  );
};
export default Schedule;
